import React, { useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { VStack } from "@design-system/VStack/VStack";
import { useAnatomyMode } from "./componentAnatomyMode";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";
import radioStyles from "./RadioPlayground.module.scss";

const options = ["cash", "accrual", "hybrid"] as const;

type Option = (typeof options)[number];

interface MatrixStateProps {
    disabled?: boolean;
    "data-forced-state"?: "hover" | "pressed" | "focus";
}

const matrixStates: ReadonlyArray<{
    id: string;
    label: string;
    props: MatrixStateProps;
}> = [
    { id: "normal", label: "Normal", props: {} },
    { id: "hover", label: "Hover", props: { "data-forced-state": "hover" } },
    {
        id: "pressed",
        label: "Pressed",
        props: { "data-forced-state": "pressed" },
    },
    { id: "focus", label: "Focus", props: { "data-forced-state": "focus" } },
    { id: "disabled", label: "Disabled", props: { disabled: true } },
];

type MatrixValue = "selected" | "other";

const MatrixRadio = ({
    isChecked,
    stateProps,
}: {
    isChecked: boolean;
    stateProps: MatrixStateProps;
}) => (
    <RadioGroup<MatrixValue>
        value={isChecked ? "selected" : "other"}
        disabled={stateProps.disabled}
    >
        <Radio
            value="selected"
            label={isChecked ? "Checked" : "Unchecked"}
            data-forced-state={stateProps["data-forced-state"]}
        />
    </RadioGroup>
);

const RadioMatrix = () => (
    <VStack className={radioStyles.matrix} gap={24} padding={24}>
        <h2 className={radioStyles.matrixTitle}>State matrix</h2>
        <div className={radioStyles.grid}>
            <span className={radioStyles.columnHeader}>State</span>
            <span className={radioStyles.columnHeader}>Unchecked</span>
            <span className={radioStyles.columnHeader}>Checked</span>
            {matrixStates.map((state) => (
                <React.Fragment key={state.id}>
                    <span className={radioStyles.rowLabel}>{state.label}</span>
                    <div className={radioStyles.cell}>
                        <MatrixRadio
                            isChecked={false}
                            stateProps={state.props}
                        />
                    </div>
                    <div className={radioStyles.cell}>
                        <MatrixRadio isChecked stateProps={state.props} />
                    </div>
                </React.Fragment>
            ))}
        </div>
        <VStack gap={12}>
            <h3 className={radioStyles.examplesTitle}>Label examples</h3>
            <HStack gap={40} align="start" wrap>
                <VStack gap={8}>
                    <span className={radioStyles.exampleLabel}>
                        Bare control
                    </span>
                    <RadioGroup value="bare">
                        <Radio value="bare" />
                    </RadioGroup>
                </VStack>
                <VStack gap={8}>
                    <span className={radioStyles.exampleLabel}>Rich label</span>
                    <RadioGroup value="rich">
                        <Radio
                            value="rich"
                            label={
                                <VStack as="span" gap={2}>
                                    <span className={radioStyles.richTitle}>
                                        Accrual accounting
                                    </span>
                                    <span
                                        className={radioStyles.richDescription}
                                    >
                                        Record income and expenses when earned.
                                    </span>
                                </VStack>
                            }
                        />
                    </RadioGroup>
                </VStack>
            </HStack>
        </VStack>
    </VStack>
);

export const RadioPlayground = () => {
    const [value, setValue] = useState<Option>("cash");
    const [isGroupDisabled, setIsGroupDisabled] = useState(false);
    const [hasDisabledItem, setHasDisabledItem] = useState(false);
    const [hasLabels, setHasLabels] = useState(true);
    // The diagram is the first option, so the group's own selection is what
    // decides whether the x-ray draws a checked or an unchecked radio.
    const anatomy = useAnatomyMode({
        component: "radio",
        variantId: value === options[0] ? "checked" : "unchecked",
        isDisabled: isGroupDisabled,
    });
    const liveSummary = `${value} · ${isGroupDisabled ? "disabled" : "enabled"}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Value</h2>
                        <ToggleGroup.Root<Option>
                            value={value}
                            onValueChange={(nextValue) => {
                                if (nextValue) {
                                    setValue(nextValue);
                                }
                            }}
                        >
                            {options.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Group disabled"
                                checked={isGroupDisabled}
                                onChange={setIsGroupDisabled}
                            />
                            <BooleanControl
                                label="Disabled option"
                                checked={hasDisabledItem}
                                onChange={setHasDisabledItem}
                            />
                            <BooleanControl
                                label="Labels"
                                checked={hasLabels}
                                onChange={setHasLabels}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive ? `x-ray · ${liveSummary}` : liveSummary
                }
                belowStage={<RadioMatrix />}
                output={JSON.stringify(
                    {
                        value,
                        disabled: isGroupDisabled,
                        disabledOption: hasDisabledItem ? "hybrid" : null,
                        labels: hasLabels,
                        forcedState: anatomy.forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <RadioGroup
                        name="basis"
                        value={value}
                        onValueChange={setValue}
                        disabled={isGroupDisabled}
                    >
                        {options.map((option, index) => (
                            <Radio
                                key={option}
                                value={option}
                                label={hasLabels ? option : undefined}
                                disabled={hasDisabledItem && index === 2}
                                // Only the first option, the one the x-ray draws.
                                data-forced-state={
                                    index === 0
                                        ? anatomy.forcedState
                                        : undefined
                                }
                            />
                        ))}
                    </RadioGroup>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
